class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    const observer = new MutationObserver(() => {
      this.setHeaderCartIconAccessibility();
    })

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: false,
      characterData: false
    })

  }

  setHeaderCartIconAccessibility() {
    if (!document.body.classList.contains('template--cart')) {
      const cartLink = document.querySelector('#cart-icon-bubble');
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink)
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartUpsellItem extends HTMLElement {
  constructor () {
    super();

    this.productHandle = this.dataset.product
    this.productId = this.dataset.productId
    this.upsellFormId = `product-form-upsell-${this.productId}`
    this.sellingPlanId = ''
    this.triggerButton = this.querySelector('.js-upsell-trigger')
    this.triggerButton.addEventListener('click', this.openUpsellModal.bind(this))
  }

  connectedCallback () {
    document.querySelector('.js-close-upsell-drawer').addEventListener('click', () => {
      this.closeUpsellModal()
    })

    document.querySelector('.js-cart-upsell-overlay').addEventListener('click', () => {
      this.closeUpsellModal()
    })

    subscribe(PUB_SUB_EVENTS.variantChange, event => {
      if (`upsell-${this.productId}` === event.data.sectionId) {
        this.toggleAddButton(event.data.variant)
        this.togglePrice(event.data.variant)
        this.updateMedia(event.data.variant)
      }
    });
  }

  closeUpsellModal () {
    document.querySelector('.drawer__inner').classList.remove('is-upsell-visible')

    setTimeout(() => {
      document.querySelector('.drawer__inner').classList.remove('is-upsell-active')
    }, 250)

    document.querySelector('#cart-upsell-product').innerHTML = ''
  }

  openUpsellModal () {
    fetch(`/products/${this.productHandle}?view=bundle-item`)
    .then(res => res.text())
    .then(html => {
      document.querySelector('#cart-upsell-product').innerHTML = html
      document.querySelector('.drawer__inner').classList.add('is-upsell-active')

      setTimeout(() => {
        document.querySelector('.drawer__inner').classList.add('is-upsell-visible')
      })

      document.querySelector('.drawer__inner').scrollTop = 0;
      const variantData = JSON.parse(document.querySelector('#cart-upsell-product [type="application/json"]').textContent)
      const initVariantId = document.querySelector(`#${this.upsellFormId} [name="id"]`).value
      this.currentVariant = variantData.find(variant => variant.id === +initVariantId)

      Array.from(document.querySelectorAll('.js-selling-plan')).map(option => {
        option.addEventListener('input', this.handleSellingPlanChange.bind(this))
      })
    })
  }

  toggleAddButton(variant) {
    const productForm = document.getElementById(this.upsellFormId);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');

    if (!addButton) return;

    if (variant.available) {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    } else {
      addButton.setAttribute('disabled', 'disabled');
      addButtonText.textContent = 'Sold out';
    }
  }

  togglePrice(variant) {
    this.currentVariant = variant;

    this.updatePriceDisplay();
  }

  updateMedia(variant) {
    if (variant.featured_image) {
      document.querySelector('#cart-upsell-product .js-upsell-image').src = variant.featured_image.src
    }
  }

  handleSellingPlanChange (event) {
    this.sellingPlanId = event.target.value
    document.querySelector(`#${this.upsellFormId} [name="selling_plan"]`).value = this.sellingPlanId

    this.updatePriceDisplay()
  }

  updatePriceDisplay () {
    let price = this.currentVariant.price;
    document.querySelector(`#price-upsell-${this.productId} .price-item--sale`).closest('.js-price').classList.add('hidden');

    Array.from(document.querySelectorAll('#cart-upsell-product .js-purchase-option')).map(option => {
      if (option.dataset.sellingPlan) {
        const sellingPlanAllocation = this.currentVariant.selling_plan_allocations.find(allocation => allocation.selling_plan_id === +option.dataset.sellingPlan)
        option.querySelector('.js-price').innerHTML = formatMoney(sellingPlanAllocation.price / 100);

        if (option.querySelector('.js-selling-plan').checked) {
          document.querySelector(`#price-upsell-${this.productId} .price-item--sale`).innerHTML = formatMoney(price / 100);
          document.querySelector(`#price-upsell-${this.productId} .price-item--sale`).closest('.js-price').classList.remove('hidden');
          price = sellingPlanAllocation.price;
        };
      } else {
        option.querySelector('.js-price').innerHTML = formatMoney(price / 100);
      }
    })

    document.querySelector(`#price-upsell-${this.productId} .price-item--regular`).innerHTML = formatMoney(price / 100);
    document.querySelector(`#price-upsell-${this.productId}`).classList.remove('visibility-hidden');
  }
}
customElements.define('cart-upsell-item', CartUpsellItem);
